import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Button } from '@components/button';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';
import { DocumentStatus } from '@models/document';
import { IconTypes } from '@pages/PolicyDetails/PolicyDetails.constants';
import { getDateFormatter, LongDateFormatWithoutSeconds } from '@utils/date';
import { format } from 'date-fns-tz';
import type { ClockFormat } from '@lio/rule-engine';

export interface ArchiveDocumentItem {
  id: string;
  documentGeneration?: { status: string } | null;
  file?: {
    downloadUrl?: string | null;
    fileName?: string | null;
    description?: string | null;
  } | null;
  documentType: { name: string };
  createdAt: string | Date;
}

export const DocumentArchiveTable = ({
  docs,
  isLoading,
  clockFormat,
  noResultsMessage = 'No documents found',
}: {
  docs: ArchiveDocumentItem[];
  isLoading: boolean;
  clockFormat: ClockFormat;
  noResultsMessage?: string;
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (docs.length === 0) {
    return (
      <Box sx={{ py: 2, px: 1.5 }}>
        <Typography variant='bodySmall' color='text.subdued'>
          {noResultsMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Table size='small' sx={{ width: '100%', tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={[archiveTableHeadCellSx, { width: '25%' }]}>Document</TableCell>
          <TableCell sx={[archiveTableHeadCellSx, { width: '30%' }]}>Filename</TableCell>
          <TableCell sx={[archiveTableHeadCellSx, { width: '20%' }]}>Date</TableCell>
          <TableCell sx={[archiveTableHeadCellSx, { width: '25%' }]}>Description</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {docs.map((doc) => {
          const isManuallyUploaded = !doc.documentGeneration;
          const hasFileDownload =
            !!doc.file?.downloadUrl &&
            (isManuallyUploaded || doc.documentGeneration?.status === DocumentStatus.Completed);
          const icon = doc.documentGeneration?.status
            ? IconTypes[doc.documentGeneration.status as keyof typeof IconTypes]
            : null;

          return (
            <TableRow key={doc.id}>
              <TableCell sx={archiveTableCellSx}>
                <Button
                  variant='text'
                  href={hasFileDownload ? (doc.file?.downloadUrl ?? undefined) : undefined}
                  disabled={!hasFileDownload}
                  endIcon={icon}
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    '&.Mui-disabled': { fontWeight: 400, cursor: 'default', color: 'text.subdued' },
                  }}
                >
                  <OverflowTooltip tooltipText={doc.documentType.name}>{doc.documentType.name}</OverflowTooltip>
                </Button>
              </TableCell>
              <TableCell sx={archiveTableCellSx}>
                <OverflowTooltip>{doc.file?.fileName ?? '-'}</OverflowTooltip>
              </TableCell>
              <TableCell sx={archiveTableCellSx}>
                <OverflowTooltip>
                  {format(new Date(doc.createdAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))}
                </OverflowTooltip>
              </TableCell>
              <TableCell sx={archiveTableCellSx}>
                <OverflowTooltip>{doc.file?.description ?? '-'}</OverflowTooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export const archiveTableHeadCellSx = {
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.subdued',
  borderBottom: '1px solid',
  borderColor: 'border.main',
  py: 1,
  px: 1.5,
  overflow: 'hidden',
};

export const archiveTableCellSx = {
  borderBottom: '1px solid',
  borderColor: 'border.main',
  py: 1,
  px: 1.5,
  overflow: 'hidden',
};
